/**
 * Modal de sincronización de stock Flexy (multi-bodega o una bodega)
 */
import React, { useEffect, useRef, useState } from 'react'
import {
  ArrowPathIcon,
  CheckCircleIcon,
  CubeIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { DEFAULT_FLEXY_IDEMPRESA } from '../../constants/flexyStock'
import { useFlexyAlmacenes } from '../integracion-flexy/hooks/useFlexyAlmacenes'
import { useFlexyStockSync } from '../../hooks/cron/useFlexyStockSync'
import type { FlexySyncMode } from '../../hooks/cron/useFlexyStockSync'

interface FlexyStockSyncModalProps {
  isOpen: boolean
  onClose: () => void
  mode?: FlexySyncMode
  autoStart?: boolean
  onCompleted?: () => void
}

const FlexyStockSyncModal: React.FC<FlexyStockSyncModalProps> = ({
  isOpen,
  onClose,
  mode = 'all',
  autoStart = false,
  onCompleted,
}) => {
  const {
    phase,
    progress,
    job,
    warehouseResult,
    error,
    isProcessing,
    startAllSync,
    startWarehouseSync,
    attachToRunningJob,
    reset,
  } = useFlexyStockSync()

  const loadAlmacenes = isOpen && mode === 'warehouse'
  const {
    almacenes,
    isLoading: isLoadingAlmacenes,
    error: almacenesError,
  } = useFlexyAlmacenes(DEFAULT_FLEXY_IDEMPRESA, loadAlmacenes)

  const [selectedWarehouse, setSelectedWarehouse] = useState<number>(0)
  const initRef = useRef(false)

  useEffect(() => {
    if (almacenes.length === 0) return
    setSelectedWarehouse((current) => {
      if (almacenes.some((almacen) => almacen.idalmacen === current)) return current
      return almacenes[0].idalmacen
    })
  }, [almacenes])

  useEffect(() => {
    if (!isOpen) {
      initRef.current = false
      return
    }
    if (initRef.current) return
    initRef.current = true

    if (autoStart && mode === 'all') {
      startAllSync()
      return
    }

    if (mode === 'all') {
      attachToRunningJob()
    }
  }, [isOpen, autoStart, mode, startAllSync, attachToRunningJob])

  useEffect(() => {
    if (phase === 'completed' && onCompleted) {
      onCompleted()
    }
  }, [phase, onCompleted])

  const handleClose = () => {
    if (!isProcessing) {
      reset()
      onClose()
    }
  }

  const handleWarehouseSync = () => {
    const warehouse = almacenes.find((almacen) => almacen.idalmacen === selectedWarehouse)
    if (!warehouse) return
    startWarehouseSync(warehouse.idalmacen, {
      codigo: warehouse.codigo,
      nombre: warehouse.nombre,
    })
  }

  if (!isOpen) return null

  const progressLabel =
    progress.totalWarehouses > 0
      ? `Sincronizando ${progress.currentWarehouseName ?? 'bodega'} (${progress.completedCount}/${progress.totalWarehouses})...`
      : 'Preparando sincronización...'

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      role="dialog"
      aria-modal="true"
      aria-label="Sincronización stock Flexy"
    >
      <div className="bg-white rounded-lg shadow-xl p-6 m-4 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-50 rounded-lg">
              <CubeIcon className="h-6 w-6 text-emerald-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">
              Sincronización Stock Flexy
            </h3>
          </div>
          <button
            type="button"
            onClick={handleClose}
            disabled={isProcessing}
            className={`text-gray-500 hover:text-gray-700 ${
              isProcessing ? 'cursor-not-allowed opacity-50' : ''
            }`}
            aria-label="Cerrar modal"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {mode === 'warehouse' && phase === 'idle' && (
          <div className="mb-6 space-y-4">
            <p className="text-sm text-gray-600">
              Sincroniza una sola bodega de forma síncrona (espera a que termine).
            </p>
            <label className="block text-sm font-medium text-gray-700" htmlFor="flexy-warehouse">
              Bodega
            </label>
            <select
              id="flexy-warehouse"
              value={selectedWarehouse || ''}
              onChange={(e) => setSelectedWarehouse(Number(e.target.value))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:bg-gray-100"
              disabled={isProcessing || isLoadingAlmacenes || almacenes.length === 0}
              aria-busy={isLoadingAlmacenes}
            >
              {isLoadingAlmacenes && <option value="">Cargando almacenes...</option>}
              {!isLoadingAlmacenes && almacenes.length === 0 && (
                <option value="">Sin almacenes disponibles</option>
              )}
              {almacenes.map((almacen) => (
                <option key={almacen.idalmacen} value={almacen.idalmacen}>
                  {almacen.label || `${almacen.nombre} (${almacen.codigo})`}
                </option>
              ))}
            </select>
            {almacenesError && (
              <p className="text-sm text-red-600" role="alert">
                {almacenesError}
              </p>
            )}
            <button
              type="button"
              onClick={handleWarehouseSync}
              disabled={isProcessing || isLoadingAlmacenes || !selectedWarehouse}
              className="w-full px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
            >
              Sincronizar bodega seleccionada
            </button>
          </div>
        )}

        {(phase === 'starting' || phase === 'polling') && mode === 'all' && (
          <div className="space-y-4 mb-6">
            <div className="flex items-center gap-3">
              <ArrowPathIcon className="h-8 w-8 text-emerald-500 animate-spin" />
              <span className="text-lg font-medium">{progressLabel}</span>
            </div>
            <div>
              <div className="flex justify-between text-sm text-gray-600 mb-2">
                <span>Progreso</span>
                <span>{progress.percent}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-emerald-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progress.percent}%` }}
                />
              </div>
            </div>
            <p className="text-xs text-gray-500">
              La sincronización multi-bodega puede tardar ~1 minuto por bodega.
            </p>
          </div>
        )}

        {phase === 'starting' && mode === 'warehouse' && (
          <div className="flex items-center gap-3 mb-6">
            <ArrowPathIcon className="h-8 w-8 text-emerald-500 animate-spin" />
            <span className="text-lg font-medium">Sincronizando bodega...</span>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-3">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-500 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        )}

        {phase === 'completed' && mode === 'all' && job && (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <CheckCircleIcon className="h-8 w-8 text-green-500" />
              <span className="text-lg font-medium text-green-700">
                Sincronización completada
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div className="p-3 bg-blue-50 rounded-lg text-center">
                <div className="text-xl font-bold text-blue-700">
                  {job.final_result?.synced_count ?? job.completed_count}
                </div>
                <div className="text-xs text-blue-600">Bodegas sincronizadas</div>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg text-center">
                <div className="text-xl font-bold text-gray-700">
                  {job.total_warehouses}
                </div>
                <div className="text-xs text-gray-600">Total bodegas</div>
              </div>
              {job.job_duration_ms != null && (
                <div className="p-3 bg-gray-50 rounded-lg text-center">
                  <div className="text-xl font-bold text-gray-700">
                    {Math.round(job.job_duration_ms / 1000)}s
                  </div>
                  <div className="text-xs text-gray-600">Duración</div>
                </div>
              )}
            </div>
            {job.results.length > 0 && (
              <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left">Bodega</th>
                      <th className="px-3 py-2 text-left">Items</th>
                      <th className="px-3 py-2 text-left">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {job.results.map((row) => (
                      <tr key={row.idalmacen} className="border-t border-gray-100">
                        <td className="px-3 py-2">{row.nombre || row.codigo}</td>
                        <td className="px-3 py-2">{row.items ?? '—'}</td>
                        <td className="px-3 py-2">
                          {row.success ? (
                            <span className="text-green-600">OK</span>
                          ) : (
                            <span className="text-red-600">{row.error ?? 'Error'}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {phase === 'completed' && mode === 'warehouse' && warehouseResult && (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <CheckCircleIcon className="h-8 w-8 text-green-500" />
              <span className="text-lg font-medium text-green-700">
                Bodega sincronizada
              </span>
            </div>
            <p className="text-sm text-gray-700">
              Items sincronizados: {warehouseResult.items ?? '—'}
            </p>
          </div>
        )}

        <div className="flex justify-end gap-3 mt-6">
          {mode === 'all' && phase === 'idle' && !autoStart && (
            <button
              type="button"
              onClick={startAllSync}
              className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
            >
              Sincronizar todas las bodegas
            </button>
          )}
          {(phase === 'completed' || phase === 'error') && (
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
            >
              Cerrar
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default FlexyStockSyncModal
